import {
  NOTE_HTML_COLOURS,
  NOTE_HTML_HREF,
  NOTE_HTML_STYLES,
  NOTE_HTML_TAGS,
  type NoteBodyFormat,
} from '@crm/contracts';
import DOMPurify from 'dompurify';
import { renderToStaticMarkup } from 'react-dom/server';
import { Markdown } from '../ui/markdown.tsx';

const STYLE_ALLOWED = new Set<string>(NOTE_HTML_STYLES);

/** The `style` of a span as the API keeps it: colour and highlight declarations with a colour value. */
const noteStyle = (style: string) =>
  style
    .split(';')
    .map((decl) => {
      const at = decl.indexOf(':');
      return [decl.slice(0, at).trim().toLowerCase(), decl.slice(at + 1).trim()] as const;
    })
    .filter(
      ([prop, value]) => STYLE_ALLOWED.has(prop) && NOTE_HTML_COLOURS.some((c) => c.test(value)),
    )
    .map(([prop, value]) => `${prop}:${value}`)
    .join(';');

// The API sanitised the HTML before storing it; this is the second line, in the viewer's browser,
// held to the same rules, on its own instance so its hooks touch no other sanitising.
const purifier = DOMPurify(window);
purifier.addHook('uponSanitizeAttribute', (node, data) => {
  const tag = node.nodeName;
  if (data.attrName === 'style') {
    data.attrValue = tag === 'SPAN' ? noteStyle(data.attrValue) : '';
    data.keepAttr = data.attrValue !== '';
  } else if (data.attrName === 'href') {
    data.keepAttr = tag === 'A' && NOTE_HTML_HREF.test(data.attrValue);
  } else if (data.attrName === 'target' || data.attrName === 'rel') {
    // Set on every kept link below, whatever was stored.
    data.keepAttr = false;
  }
});
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName !== 'A' || !node.hasAttribute('href')) return;
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
});

const PURIFY = {
  ALLOWED_TAGS: [...NOTE_HTML_TAGS],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
  ALLOWED_URI_REGEXP: NOTE_HTML_HREF,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

/** An `html` note as it may be put in the page. */
export const purifyNoteHtml = (html: string) => purifier.sanitize(html, PURIFY);

/**
 * A note's body in its format: an `html` note as its sanitised HTML, a `markdown` one
 * (written before the rich text editor) through `Markdown`, which also renders raw HTML in it.
 */
export function NoteBody({ body, format }: { body: string; format: NoteBodyFormat }) {
  if (format !== 'html') return <Markdown html>{body}</Markdown>;
  return (
    <div
      className="rich-text text-sm leading-relaxed break-words"
      dangerouslySetInnerHTML={{ __html: purifyNoteHtml(body) }}
    />
  );
}

/** Formatting the editor cannot keep (tables, images, headings, code, quotes, rules). */
const EDITOR_DROPS = /<(table|img|h[1-6]|pre|code|blockquote|hr)\b/i;

/**
 * True when saving an edit of this Markdown note would drop some of its formatting. Takes the
 * HTML `noteHtmlForEditing` gave, so the Markdown is not rendered again on every keystroke.
 */
export const editingDropsFormatting = (editingHtml: string, format: NoteBodyFormat) =>
  format !== 'html' && EDITOR_DROPS.test(editingHtml);

/** A note's body as HTML for the editor: a Markdown note becomes HTML when its author edits it. */
export const noteHtmlForEditing = (body: string, format: NoteBodyFormat) =>
  format === 'html' ? body : renderToStaticMarkup(<Markdown html>{body}</Markdown>);

/** Said before saving an edit that would drop some of an old note's formatting. */
export function DropsFormatting() {
  return (
    <p className="text-muted-foreground text-xs" role="note" data-testid="drops-formatting">
      This note has tables, images, headings or code that the editor cannot keep. Saving it keeps
      the text but not that formatting.
    </p>
  );
}
